import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HighlightBadge, { HighlightBadgeType } from 'components/HighlightBadge';
import ToggleSwitch from 'components/ToggleSwitch';
import { PaymentMethod as PaymentMethodEnum } from 'enums/PaymentMethod';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData } from 'selectors/getApplicationData';
import { getReportsState } from 'selectors/getOffers';
import { updatePaymentMethod, getOffers } from 'thunks';

import styles from './PaymentMethodSwitch.module.scss';

interface PaymentMethodSwitchProps {
  disabled?: boolean;
}

export const getPaymentMethodLabel = (paymentMethod?: PaymentMethodEnum) => {
  if (paymentMethod === PaymentMethodEnum.ACH) {
    return 'month';
  }
  return 'paycheck';
};

const PaymentMethodSwitch = ({ disabled = false }: PaymentMethodSwitchProps) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application, isLoading: isApplicationLoading } = useSelector(getApplicationData);
  const { isLoading: isLoadingOffers } = useSelector(getReportsState);

  const isPaycheckDirectDeposit = application?.paymentMethod === PaymentMethodEnum.Payroll;

  const handleToggleChange = async (checked: boolean) => {
    if (isApplicationLoading || disabled) return;

    const newPaymentMethod = checked ? PaymentMethodEnum.Payroll : PaymentMethodEnum.ACH;

    // Only update if actually changing from current payment method
    if (newPaymentMethod !== application?.paymentMethod && application?.id) {
      await dispatchWithUnwrap(
        updatePaymentMethod({
          applicationId: application.id,
          paymentMethod: newPaymentMethod,
        }),
      );

      if (application?.id) dispatch(getOffers(application.id));
    }
  };

  const getPaymentMethodSwitchLabel = () =>
    isPaycheckDirectDeposit ? 'Paycheck direct deposit' : 'Pay from checking account';
  const getBadgeData = () => ({
    type: isPaycheckDirectDeposit ? HighlightBadgeType.Success : HighlightBadgeType.Gray,
    text: isPaycheckDirectDeposit ? 'Most savings' : 'Savings turned off',
  });
  const { type, text } = getBadgeData();

  return (
    <div className={styles.paymentMethodSwitchContainer}>
      <div>
        <HighlightBadge type={type} customText={text} />
        <p className={styles.paymentMethodSwitchLabel}>{getPaymentMethodSwitchLabel()}</p>
      </div>
      <ToggleSwitch
        checked={isPaycheckDirectDeposit}
        onChange={handleToggleChange}
        disabled={disabled || isApplicationLoading || isLoadingOffers}
      />
    </div>
  );
};

export default PaymentMethodSwitch;
